import jwtDecode from "jwt-decode";
import { app as teamsApp, chat as teamsChat } from "@microsoft/teams-js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { components } from "react-select";
import Cookies from "js-cookie";
import CryptoJS from "crypto-js";
import {
  REACT_APP_HIRESELECT_URL,
  REACT_APP_URL,
  sskowk,
  ENVIRONMENT,
  IS_AU_INSTANCE,
} from "utils/environmentVariables";
import { IdentityProviderResponse } from "app/containers/Login/types";
import { IConfigCatClient, User } from "configcat-react";
import { AppDispatch } from "./redux/store";
import {
  setConfigCatFlag,
  setConfigCatFlagStatus,
} from "app/containers/Global/slice";
import { CONFIG_CAT_FLAGS } from "app/components/GlobalNavbar/constants";
import { UserInfo } from "app/containers/Global/types";
import { DEVELOP_TESTING_USER_ACCOUNT_IDS } from "./constants";

// Question: Is there a better way to store these global interfaces? Maybe into its own types file
declare global {
  interface DecodedJWT {
    iss: string;
    jti: number;
    iat: number;
    nbf: number;
    exp: number;
    sub: number; // userAccountId
    scopes: string[];
    internalMode?: "god" | null;
    hierarchyContext?: {
      childCompanyAccountId: number;
      originalCompanyAccountId: number;
      originalUserAccountId?: number;
      proxyUserAccountId?: number;
      accessLevel: "read" | "admin";
    };
  }
  interface Window {
    analytics: any;
    Appcues: {
      identify: (userId: string, userProperties: object) => void;
      reset: () => void;
    };
  }
}

// Copied code from original talent insights
export const downloadCSV = (tableName: string, data: string[][]) => {
  // this version of the function should work for all browsers
  const csv = convertArrayOfArraysToCSV(data);
  if (csv === null) {
    return null;
  }

  const filename = `${tableName}.csv`;

  const BOM = new Uint8Array([0xef, 0xbb, 0xbf]); // Enforces UTF-8 encoding
  const blob = new Blob([BOM, csv], { type: "text/csv;" });

  const link = document.createElement("a");
  if (link.download !== undefined) {
    // feature detection, Browsers that support HTML5 download attribute
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// Copied code from original talent insights
const convertArrayOfArraysToCSV = (data: string[][]) => {
  if (data == null || !data.length) {
    return null;
  }

  const columnDelimiter = ",";
  const lineDelimiter = "\n";

  let result = "";
  data.forEach((row, idx) => {
    row.forEach((item, idy) => {
      // Add a comma to the beginning of each cell unless its the first one
      if (idy > 0) {
        result += columnDelimiter;
      }

      let itemToDisplay = item;
      if (
        item === null ||
        item === undefined ||
        !(typeof item === "string" || typeof item === "number")
      ) {
        itemToDisplay = "-";
      }
      itemToDisplay = itemToDisplay.replace(/"/g, "'");

      result += `"${itemToDisplay}"`;
    });

    if (idx < data.length - 1) {
      result += lineDelimiter;
    }
  });
  return result;
};

export const setCookieItem = (
  key: string,
  value: string,
  numberOfDays: number
) => {
  const now = new Date();
  // set the time to be now + numberOfDays
  now.setTime(now.getTime() + numberOfDays * 60 * 60 * 24 * 1000);
  document.cookie = `${key}=${value};     expires=${now.toUTCString()}; path=/`;
};

export const getUserIdFromCookie = () => {
  try {
    // Getting the token from the url should only be temporary up until we have the proper domain set up.
    const token = getUserToken();

    if (!token) {
      throw new Error();
    }
    const decodedToken: DecodedJWT = jwtDecode(token);

    return {
      // Inside a child company the API acts as the proxy user, and only that user can be looked
      // up there. `sub` is still the parent admin, whose own company is not the active one.
      userId: decodedToken.hierarchyContext?.proxyUserAccountId ?? decodedToken.sub,
      godMode: decodedToken.internalMode === "god",
      hierarchyContext: decodedToken.hierarchyContext ?? null,
    };
  } catch (e) {
    return { userId: null, godMode: false, hierarchyContext: null };
  }
};

export const logUserOut = (redirect: boolean = true) => {
  const cookieConfig = getCookieConfig();
  Cookies.remove("critToken", cookieConfig);
  Cookies.remove("ssoLoginSuccess", cookieConfig);
  localStorage.clear();
  window.Appcues?.reset();

  if (redirect) {
    window.location.replace("/");
  }
};

// Generated using openAI's GPT-3, retuns a random number between min and max but skweed the distribution towards the min
export function generateNumber(min: number, max: number) {
  let rand = Math.random();
  let num = Math.round(min + rand * rand * max - 1);
  return num;
}

// Wraps the entries in a generic to provide typescript support
type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

export const getEntries = <T extends object>(obj: T) =>
  Object.entries(obj) as Entries<T>;

export const calculateDaysInBetween = (
  date1: string,
  date2?: string
): number => {
  const date_1 = new Date(date1).valueOf();
  const date_2 = date2 ? new Date(date1).valueOf() : Date.now();
  const diff = date_2 - date_1;
  return Math.ceil(diff / (1000 * 3600 * 24));
};
export const getSelectProps = (
  type?: "search",
  customStyles?: { fixedWidth?: number; controlBgColor?: string }
) => {
  switch (type) {
    case "search":
    default: {
      const { fixedWidth, controlBgColor } = customStyles || {};
      return {
        selectStyles: {
          control: (base: any) => ({
            ...base,
            flexDirection: "row-reverse",
            width: fixedWidth,
            background: controlBgColor,
          }),
          clearIndicator: (base: any) => ({
            ...base,
            position: "absolute",
            right: 0,
          }),
          menu: (base: any) => ({
            ...base,
            position: "absolute",
            right: 0,
            width: "100%",
            zIndex: 9999,
          }),
          placeholder: (base: any) => ({
            ...base,
            fontSize: "14px",
          }),
        },
        components: {
          IndicatorSeparator: () => null,
          DropdownIndicator: (props: any) =>
            components.DropdownIndicator && (
              <components.DropdownIndicator {...props}>
                <FontAwesomeIcon icon="search" />
              </components.DropdownIndicator>
            ),
        },
      };
    }
  }
};

// Complexity: O(n * m)
export const newCombineArrays = (...arrayOfArrays: any[]): any[] => {
  const max: number = arrayOfArrays.reduce(
    (acc, curr) => Math.max(acc, curr.length),
    0
  );
  const returnArr: any[] = [];

  for (let i = 0; i < max; i++) {
    arrayOfArrays.forEach((arrayElm) => {
      if (arrayElm[i] !== undefined) {
        returnArr.push(arrayElm[i]);
      }
    });
  }

  return returnArr;
};

export const decodeStringArray = (array: string) => {
  try {
    return JSON.parse(array);
  } catch (e) {
    return [];
  }
};

/**
 * Email validation functions based on RFC 5322 standard
 */

// Validates quoted local part of email (between quotes)
const isValidQuotedLocalPart = (localPart: string): boolean => {
  // For quoted strings, almost anything is allowed except unescaped double quotes and linebreaks
  const quotedContentRegex = /^"(?:[^"]|\\")*"$/;
  return quotedContentRegex.test(localPart);
};

// Validates unquoted local part of email
const isValidUnquotedLocalPart = (localPart: string): boolean => {
  // For unquoted strings, only certain characters are allowed
  const localRegex = /^[A-Z0-9.!#$%&'*+\-/=?^_`{|}~]+$/i;
  if (!localRegex.test(localPart)) return false;

  // Check for dots at the beginning or end of local part (invalid per RFC 5322)
  if (localPart.startsWith(".") || localPart.endsWith(".")) return false;

  // Check for consecutive dots in local part (invalid per RFC 5322)
  if (localPart.includes("..")) return false;

  return true;
};

// Validates the domain part of the email
const isValidDomainPart = (domainPart: string): boolean => {
  // Check for consecutive dots which are invalid
  if (domainPart.includes("..")) return false;

  // Domain regex: allows for all valid TLDs and ensures proper domain formatting
  const domainRegex =
    /^[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?(\.[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?)+$/i;
  return domainRegex.test(domainPart);
};

// Main email validation function
export const isValidEmail = (email: string = ""): boolean => {
  if (!email?.includes("@")) return false;

  // Split email into local and domain parts
  // Use lastIndexOf to handle cases where @ might appear in quoted parts
  const lastAtIndex = email.lastIndexOf("@");
  const localPart = email.substring(0, lastAtIndex);
  const domainPart = email.substring(lastAtIndex + 1);

  // If after splitting we don't have both parts, it's invalid
  if (!localPart || !domainPart) return false;

  // Validate local part (RFC 5322)
  const isLocalPartValid =
    localPart.startsWith('"') && localPart.endsWith('"')
      ? isValidQuotedLocalPart(localPart)
      : isValidUnquotedLocalPart(localPart);

  if (!isLocalPartValid) return false;

  // Validate domain part
  return isValidDomainPart(domainPart);
};

// create a function that takes in a string and it return back the string but with the first letter capitalized
export const capitalizeWord = (word: string = ""): string => {
  if (!word) return "";
  return word[0].toUpperCase() + word.slice(1);
};

export const isInternalCompanyAccount = (companyAccountId?: number) => {
  const INTERNAL_TESTING_COMPANY_ACCOUNTS = [69669, 236473];
  if (
    companyAccountId &&
    INTERNAL_TESTING_COMPANY_ACCOUNTS.includes(companyAccountId)
  ) {
    return true;
  }
  return false;
};

export const getHasAccessToChatbot = (companyAccountId?: number) => {
  if (ENVIRONMENT === "dev") {
    return true;
  }
  if (ENVIRONMENT === "prod") {
    return isInternalCompanyAccount(companyAccountId);
  }
};

/**
 * Decrypt string.
 *
 * @link https://stackoverflow.com/questions/41222162/encrypt-in-php-openssl-and-decrypt-in-javascript-cryptojs Reference.
 * @link https://stackoverflow.com/questions/25492179/decode-a-base64-string-using-cryptojs Crypto JS base64 encode/decode reference.
 * @link https://github.com/CriteriaCorp/new-oda/blob/master/src/utils/Encryption.js Reference.
 * @param string encryptedString The encrypted string to be decrypt.
 * @param string key The key.
 * @return string Return decrypted string.
 */

// this function will take in a string and decrypt it using the key "sskowk" from environment variables
export const decryptSurveyInfo = (
  encryptedString: string,
  key: string = sskowk
) => {
  const ENCRYPT_METHOD = "AES-256-CBC" as const;
  const aesNumber = ENCRYPT_METHOD.match(/\d+/)?.[0];
  let encryptMethodLength = parseInt(aesNumber ?? "0");
  encryptMethodLength = encryptMethodLength / 4;

  // Parse the encrypted data
  const json = JSON.parse(
    CryptoJS.enc.Utf8.stringify(CryptoJS.enc.Base64.parse(encryptedString))
  );

  // Parse the salt and IV
  const salt = CryptoJS.enc.Hex.parse(json.salt);
  const iv = CryptoJS.enc.Hex.parse(json.iv);

  const encrypted = json.ciphertext; // no need to base64 decode.
  const iterations = parseInt(json.iterations) || 999;

  const hashKey = CryptoJS.PBKDF2(key, salt, {
    hasher: CryptoJS.algo.SHA512,
    keySize: encryptMethodLength / 8,
    iterations: iterations,
  });

  const decrypted = CryptoJS.AES.decrypt(encrypted, hashKey, {
    mode: CryptoJS.mode.CBC,
    iv: iv,
  });

  return JSON.parse(decrypted.toString(CryptoJS.enc.Utf8));
};

export const handleKeyDown = (
  e: React.KeyboardEvent,
  handleClick?: () => void
) => {
  if (e.key === "Enter" || e.key === " ") {
    // Perform the same action as onClick
    handleClick?.();
  }
};

export function toSorted<T>(
  array: T[],
  compareFn?: (a: T, b: T) => number
): T[] {
  return [...array].sort(compareFn);
}

export const getS = (count: number) => (count === 1 ? "" : "s");

export const getHasOrHave = (count: number) => (count === 1 ? "has" : "have");

export const getPeopleOrPerson = (total: number) =>
  total === 1 ? "person" : "people";

export function numberToWord(number: number): string {
  const words = [
    "zero",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
  ];
  const isPositive = number >= 0;
  if (isPositive && number < words.length) {
    return words[number];
  }

  return number.toString();
}

export const isUTCTimeBeforeOrAfterLocal = (
  utcTimeString: string
): "before" | "equal" | "after" => {
  // Create a Date object for the current local time
  const localTime = new Date();

  // Create a Date object for the UTC time string
  // Assuming the UTC time string is for today's date
  const today = localTime.toISOString().split("T")[0]; // Get today's date in YYYY-MM-DD format
  const utcDateTimeString = `${today}T${utcTimeString}Z`; // Construct the full UTC date-time string
  const utcTimeConvertedToLocal = new Date(utcDateTimeString);

  if (utcTimeConvertedToLocal < localTime) {
    return "before";
  } else if (utcTimeConvertedToLocal > localTime) {
    return "after";
  } else {
    return "equal";
  }
};

export function mockApiCall<T>(
  responseValue: T,
  delay: number = 1000
): Promise<T> {
  return new Promise<T>((resolve) => {
    setTimeout(() => {
      resolve(responseValue);
    }, delay);
  });
}

export const getIdpLogoutUrl = (idpInfo: IdentityProviderResponse) => {
  if (!idpInfo) {
    return "/";
  }

  // remove the existing cookie if any.
  const cookieConfig = getCookieConfig();
  Cookies.remove("critToken", cookieConfig);
  Cookies.remove("ssoLoginSuccess", cookieConfig);
  localStorage.clear();

  return `${idpInfo.cognitoBaseUrl?.slice(0, -16)}logout?client_id=${
    idpInfo.clientId
  }&logout_uri=${REACT_APP_HIRESELECT_URL}/`;
};

export const getUserConfigCat = async (
  configCatUser: User,
  configCatClient: IConfigCatClient,
  dispatch: AppDispatch
) => {
  const flags = Object.values(CONFIG_CAT_FLAGS).map((flag) => ({
    flagName: flag.rawFlagName,
    key: flag.savedFlagName,
    defaultValue: flag.defaultValue,
  }));

  await Promise.all(
    flags.map((flag) =>
      getAsyncFlag(configCatClient, configCatUser, dispatch, flag)
    )
  );

  dispatch(setConfigCatFlagStatus("succeeded"));
};

/**
 * Reusable helper to load ConfigCat flags into Redux state.
 * Can be called from both authenticated (App.tsx) and loginless flows.
 */
export const loadConfigCatFlags = async (
  configCatClient: IConfigCatClient,
  dispatch: AppDispatch,
  userAccountId: string,
  companyAccountId: string,
  emailAddress = ""
) => {
  dispatch(setConfigCatFlagStatus("loading"));
  await configCatClient.forceRefreshAsync();
  const subdomainLocaleInstancePrefix = IS_AU_INSTANCE ? "AU" : "US";

  const currUser: User = new User(userAccountId, emailAddress, undefined, {
    companyAccountId,
    companyAccountIdWithLocale: `${subdomainLocaleInstancePrefix}${companyAccountId}`,
    instance: subdomainLocaleInstancePrefix,
  });
  getUserConfigCat(currUser, configCatClient, dispatch);
};

export const getAsyncFlag = async (
  configCatClient: IConfigCatClient,
  configCatUser: User,
  dispatch: AppDispatch,
  configCatFlagConfig: {
    flagName: string;
    key?: string;
    defaultValue?: any;
  }
): Promise<void> => {
  const envOverrideKey = `REACT_APP_CONFIGCAT_${configCatFlagConfig.flagName.toUpperCase().replace(/-/g, "_")}`;
  const envOverride = process.env[envOverrideKey];

  const flagValue =
    envOverride !== undefined
      ? envOverride === "true"
      : await configCatClient.getValueAsync(
          configCatFlagConfig.flagName,
          configCatFlagConfig?.defaultValue ?? false,
          configCatUser
        );

  dispatch(
    setConfigCatFlag({
      key: configCatFlagConfig.key ?? configCatFlagConfig.flagName,
      value: flagValue,
    })
  );
};

export const getCookieConfig = () => ({
  path: "/",
  domain:
    new URL(REACT_APP_URL).hostname === "localhost"
      ? "localhost"
      : ".criteriacorp.com",
});

const getQueryParams = (): URLSearchParams => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams;
};

export const getUserToken = () => {
  // if token is passed via the url then we set it in the local storage
  const urlParamToken = getQueryParams().get("critToken");
  if (urlParamToken) {
    localStorage.setItem("critToken", urlParamToken);
  }

  const cookieToken = Cookies.get("critToken");
  // if a new token is found in the cookie then we set it in local storage, remove it from the cookie and return it.
  // A cookie matching our current token is the one navigateToPreHire just set for HireSelect, so leave it in place.
  if (cookieToken && cookieToken !== localStorage.getItem("critToken")) {
    localStorage.setItem("critToken", cookieToken);
    Cookies.remove("critToken", getCookieConfig());
    return cookieToken;
  }

  // first we check if the token is in local storage if it is then we return it
  const token = localStorage.getItem("critToken");
  if (token) {
    return token;
  }

  return null;
};

export const setUserToken = (token: string) => {
  localStorage.setItem("critToken", token);
  // getUserToken() prefers a critToken cookie over localStorage whenever one
  // exists — clear it here so a stale cookie (e.g. left over from the initial
  // login handoff) can't silently override the token we just set (this is how
  // switching into a child company was reverting back to the parent's token).
  Cookies.remove("critToken", getCookieConfig());
};

export const navigateToPreHire = (openNewWindow?: boolean) => {
  // Before redirecting back to hireselect we need to make sure that we set the critToken in the cookie
  // so that the user is logged in when they get to the hireselect page
  const token = getUserToken();
  if (token) {
    const cookieConfig = getCookieConfig();
    Cookies.set("critToken", token, cookieConfig);
  }

  if (openNewWindow) {
    window.open(REACT_APP_HIRESELECT_URL, "_blank");
  } else {
    window.location.href = REACT_APP_HIRESELECT_URL;
  }
};

export const getUserName = (user?: UserInfo | null) => {
  if (!user) return "";
  if (!user.firstName || !user.lastName) return user.emailAddress;
  return `${user.firstName} ${user.lastName}`.trim();
};

export const doesUserHaveAccessToTestingFeature = (
  userAccountId: number | null
) =>
  userAccountId &&
  DEVELOP_TESTING_USER_ACCOUNT_IDS.includes(userAccountId) &&
  !IS_AU_INSTANCE;

export const formatRelativeTime = (isoDate: string): string => {
  const now = new Date();
  const then = new Date(isoDate);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "today";
  if (diffDays === 1) return "yesterday";
  if (diffDays < 30) return `${diffDays} days ago`;
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return months === 1 ? "1 month ago" : `${months} months ago`;
  }
  const years = Math.floor(diffDays / 365);
  return years === 1 ? "1 year ago" : `${years} years ago`;
};

export const openTeamsChat = async (emailAddress: string) => {
  try {
    await teamsApp.initialize();
    await teamsChat.openChat({ user: emailAddress });
  } catch {
    const url = `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(emailAddress)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }
};
